import React, { useContext, useEffect, useState } from 'react';
import { ShopContext } from '../context/shopcontext';
import Title from '../components/Title';
import { assets } from '../assets/assets';
import CartTotal from '../components/CartTotal';
import axios from 'axios';
import { FaTrashAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';

const Cart = () => {
  const { currency, cartItems, updateQuantity, navigate, backendUrl } = useContext(ShopContext);
  const [cartData, setCartData] = useState([]);
  const [productMap, setProductMap] = useState({}); // Holds fetched products by ID
  const [loading, setLoading] = useState(true); // ✅ loading state

  // Step 1: Build local cart state from global cartItems
  useEffect(() => {
    const tempData = [];
    for (const id in cartItems) {
      for (const size in cartItems[id]) {
        if (cartItems[id][size] > 0) {
          tempData.push({ _id: id, size, quantity: cartItems[id][size] });
        }
      }
    }
    setCartData(tempData);
  }, [cartItems]);

  // Step 2: Fetch all products in cart in batch
  useEffect(() => {
    const fetchProducts = async () => {
      if (cartData.length === 0) {
        setProductMap({});
        setLoading(false);
        return;
      }

      setLoading(true); // start loading
      try {
        const productIds = cartData.map((item) => item._id);
        const response = await axios.post(`${backendUrl}/api/product/list-by-ids`, {
          productIds,
        });

        if (response.data.success) {
          const fetchedProducts = response.data.products;
          const newProductMap = {};

          cartData.forEach((item) => {
            const product = fetchedProducts.find((p) => p._id === item._id);
            newProductMap[item._id] = product || null; // ✅ assign null if product not found
          });

          setProductMap(newProductMap);
        } else {
          toast.error('Failed to fetch cart products.');
        }
      } catch (error) {
        console.error('Error fetching cart products:', error);
        toast.error('Error fetching cart products.');
      } finally {
        setLoading(false); // done loading
      }
    };

    fetchProducts();
  }, [cartData, backendUrl]);

  // ✅ Step 3: Helper - check if all products loaded
  const allProductsLoaded = cartData.every((item) => item._id in productMap);

  // Handle quantity change with auto-remove
  const handleQuantityChange = (id, size, newQuantity, product) => {
    // When reaching zero, remove the item
    if (newQuantity <= 0) {
      updateQuantity(id, size, 0, product); // This will remove the item from cartItems
    } else {
      updateQuantity(id, size, newQuantity, product);
    }
  };

  return (
    <div className={`border-t pt-32 px-4 ${loading ? 'min-h-screen' : ''}`}>
      <div className="text-2xl mb-3">
        <Title text1={'YOUR '} text2={'CART'} />
      </div>

      {/* Step 4: Loading / Empty state */}
      {loading || !allProductsLoaded ? (
        <Spinner />
      ) : cartData.length === 0 ? (
        <div className="text-center py-20 text-[#4c0f2e] text-lg">
          <p>Your cart is empty :(</p>
          <button
            onClick={() => navigate('/collection')}
            className="mt-5 px-3 py-2 bg-[#4c0f2e] text-white rounded-md hover:from-[#FFC3A1] hover:to-[#4c0f2e]"
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <div>
          {cartData.map((item, index) => {
            const product = productMap[item._id];

            if (!product) {
              return (
                <div
                  key={index}
                  className="py-4 border-t border-b text-[#9D4A54] flex justify-between items-center"
                >
                  <div className="flex items-center gap-6">
                    <FaTrashAlt
                      onClick={() => updateQuantity(item._id, item.size, 0, product)}
                      className="text-[#4c0f2e] text-center w-5 cursor-pointer hover:scale-110 transition-transform"
                    />
                    <div>
                      <p className="text-xs sm:text-lg font-medium text-gray-400">
                        This product is no longer available.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-center">
                    <input
                      disabled
                      className="border rounded-md max-w-10 max-h-10 text-center sm:max-w-20 px-2 py-1 text-gray-400 bg-gray-100"
                      type="number"
                      value={item.quantity}
                    />
                    <FaTrashAlt
                      onClick={() => updateQuantity(item._id, item.size, 0, product)}
                      className="text-[#4c0f2e] w-5 cursor-pointer hover:scale-110 transition-transform"
                    />
                  </div>
                </div>
              );
            }

            return (
              <div
                key={index}
                className="py-4 border-t border-b text-[#4c0f2e] flex justify-between items-center"
              >
                <div
                  className="flex items-center gap-6 "
                  onClick={() => navigate(`/product/${product._id}`)}
                >
                  <img className="w-16 sm:w-20" src={product.image[0]} alt={product.name} />
                  <div>
                    <p className="text-md sm:text-lg font-bold font-forum">{product.name}</p>
                    <div className="flex items-center gap-5 mt-2">
                      <p className="text-[#4c0f2e]">₹ {product.price}</p>
                      {product.sizes?.length > 0 && item.size !== 'default' && (
                        <p className="px-2 rounded-md sm:px-3 sm:py-1 border-[#F0997D] bg-[#A75D5D] text-white">
                          {item.size}
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-[#B33A3A] mt-1">
                      Hurry up! Only{' '}
                      {product.sizes?.length > 0 && item.size !== 'default'
                        ? product.sizes.find((s) => s.size === item.size)?.quantity || 0
                        : product.stockQuantity || 0}{' '}
                      left in stock.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-[#F0E0DC] rounded-md overflow-hidden w-[80px] sm:w-[100px]">
                    <button
                      onClick={() => {
                        // If quantity is already 1, removing it completely
                        if (item.quantity === 1) {
                          handleQuantityChange(item._id, item.size, 0, product);
                        } else {
                          handleQuantityChange(item._id, item.size, item.quantity - 1, product);
                        }
                      }}
                      className="flex-1 px-2 py-[6px] text-[#4c0f2e] text-lg font-bold hover:bg-[#F0E0DC] transition-all"
                    >
                      –
                    </button>
                    <div className="flex-1 text-center text-sm font-medium text-[#4c0f2e] select-none">
                      {item.quantity}
                    </div>
                    <button
                      onClick={() =>
                        handleQuantityChange(item._id, item.size, item.quantity + 1, product)
                      }
                      className="flex-1 px-2 py-[6px] text-[#4c0f2e] text-lg font-bold hover:bg-[#F0E0DC] transition-all"
                    >
                      +
                    </button>
                  </div>

                  <FaTrashAlt
                    onClick={() => handleQuantityChange(item._id, item.size, 0, product)}
                    className="text-[#4c0f2e] w-5 cursor-pointer hover:scale-110 transition-transform"
                  />
                </div>
              </div>
            );
          })}

          {/* Cart Total */}
          <div className="flex justify-end my-20">
            <div className="w-full sm:w-[450px]">
              <CartTotal cartData={cartData} productMap={productMap} />
              <div className="w-full text-end">
                <button
                  onClick={() => {
                    let hasStockIssue = false;

                    cartData.forEach((item) => {
                      const product = productMap[item._id];

                      if (!product) {
                        hasStockIssue = true;
                        toast.error(`Product has been removed from stock.`);
                        return;
                      }

                      let availableQuantity = 0;

                      if (product.sizes?.length > 0 && item.size !== 'default') {
                        const selectedSize = product.sizes.find((s) => s.size === item.size);
                        availableQuantity = selectedSize ? selectedSize.quantity : 0;
                      } else {
                        availableQuantity = product.stockQuantity || 0;
                      }

                      if (item.quantity > availableQuantity) {
                        hasStockIssue = true;
                        toast.error(
                          `Product "${product.name}" has only ${availableQuantity} left in stock.`
                        );
                      }
                    });

                    if (!hasStockIssue) {
                      navigate('/place-order');
                    }
                  }}
                  className="rounded-md bg-[#4c0f2e] text-white text-sm my-8 py-3 px-3 hover:from-[#FFC3A1] hover:to-[#4c0f2e]"
                >
                  PROCEED TO CHECKOUT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
